import type { ILogger } from '../logging/Logger';
import type { IBookService, ServiceResult, PaginationParams, PaginatedResult } from '../interfaces/IDataService';
import { publicAPI, type Book as APIBook } from '../../../lib/api';
import { ValidationError } from '../../../utils/errorHandler';
import type { Book } from '../../../types/index';

/**
 * BookService Implementation
 * 
 * Provides book management functionality with proper API integration.
 * Uses the publicAPI.getPublishedBooks() method for data retrieval.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */
export class BookService implements IBookService {
  private readonly logger: ILogger;

  constructor(logger: ILogger) {
    this.logger = logger;
  }

  /**
   * Convert API Book to interface Book
   */
  private mapApiBook(apiBook: APIBook): Book {
    return {
      ...apiBook,
      cover_image: apiBook.cover_image || undefined, // Convert null to undefined
    } as Book;
  }

  /**
   * Get all books with pagination
   */
  async getAll(params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Book>>> {
    try {
      this.logger.debug('BookService.getAll called', 'BookService', { params });

      const { page = 1, limit = 10, sortBy = 'created_at', sortOrder = 'desc' } = params;

      // Validate pagination parameters
      if (page < 1 || limit < 1 || limit > 100) {
        return {
          success: false,
          error: 'Invalid pagination parameters. Page must be >= 1, limit must be between 1 and 100.'
        };
      }

      const apiBooks = await publicAPI.getPublishedBooks();
      let books = apiBooks.map(book => this.mapApiBook(book));

      // Apply sorting
      books = this.applySorting(books, sortBy, sortOrder);

      // Apply pagination
      const total = books.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedBooks = books.slice(startIndex, endIndex);

      const result: PaginatedResult<Book> = {
        items: paginatedBooks,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Books retrieved successfully', 'BookService', {
        total,
        page,
        itemCount: paginatedBooks.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in BookService.getAll', 'BookService', { 
        error: (error as Error).message,
        params 
      });
      
      if (error instanceof ValidationError) {
        return { success: false, error: error.message };
      }
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching books' 
      };
    }
  }

  /**
   * Get a book by ID
   */
  async getById(id: string): Promise<ServiceResult<Book>> {
    try {
      this.logger.debug('BookService.getById called', 'BookService', { id });

      if (!id || typeof id !== 'string') {
        return { success: false, error: 'Invalid book ID provided' };
      }

      const apiBooks = await publicAPI.getPublishedBooks();
      const apiBook = apiBooks.find(book => book.id === id);

      if (!apiBook) {
        this.logger.warn('Book not found', 'BookService', { id });
        return { success: false, error: 'Book not found' };
      }

      const book = this.mapApiBook(apiBook);
      this.logger.debug('Book retrieved successfully', 'BookService', { 
        id, 
        title: book.title 
      });

      return { success: true, data: book };

    } catch (error) {
      this.logger.error('Error in BookService.getById', 'BookService', { 
        error: (error as Error).message,
        id 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching the book' 
      };
    }
  }

  /**
   * Get currently reading books
   */
  async getCurrentlyReading(): Promise<ServiceResult<Book[]>> {
    try {
      this.logger.debug('BookService.getCurrentlyReading called', 'BookService');

      const apiBooks = await publicAPI.getPublishedBooks();
      const currentlyReading = apiBooks
        .filter(book => book.status === 'currently_reading')
        .map(book => this.mapApiBook(book));

      this.logger.debug('Currently reading books retrieved', 'BookService', {
        count: currentlyReading.length
      });

      return { success: true, data: currentlyReading };

    } catch (error) {
      this.logger.error('Error in BookService.getCurrentlyReading', 'BookService', { 
        error: (error as Error).message 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching currently reading books' 
      };
    }
  }

  /**
   * Get finished books with pagination
   */
  async getFinished(params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Book>>> {
    try {
      this.logger.debug('BookService.getFinished called', 'BookService', { params });

      const apiBooks = await publicAPI.getPublishedBooks();
      const finishedBooks = apiBooks
        .filter(book => book.status === 'completed' && book.end_date)
        .map(book => this.mapApiBook(book));

      // Sort by completion date (most recent first)
      const sortedBooks = finishedBooks.sort((a, b) => {
        const dateA = a.end_date ? new Date(a.end_date).getTime() : 0;
        const dateB = b.end_date ? new Date(b.end_date).getTime() : 0;
        return dateB - dateA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedBooks.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

      const result: PaginatedResult<Book> = {
        items: paginatedBooks,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Finished books retrieved', 'BookService', {
        total,
        page,
        itemCount: paginatedBooks.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in BookService.getFinished', 'BookService', { 
        error: (error as Error).message,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching finished books' 
      };
    }
  }

  /**
   * Get books by status with pagination
   */
  async getByStatus(status: string, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Book>>> {
    try {
      this.logger.debug('BookService.getByStatus called', 'BookService', { status, params });

      if (!status) {
        return { success: false, error: 'Status parameter is required' };
      }

      const validStatuses = ['want_to_read', 'currently_reading', 'completed', 'did_not_finish'];
      if (!validStatuses.includes(status)) {
        return { 
          success: false, 
          error: `Invalid status. Must be one of: ${validStatuses.join(', ')}` 
        };
      }

      const apiBooks = await publicAPI.getPublishedBooks();
      const filteredBooks = apiBooks
        .filter(book => book.status === status)
        .map(book => this.mapApiBook(book));

      // Apply sorting (most recent first)
      const sortedBooks = filteredBooks.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedBooks.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

      const result: PaginatedResult<Book> = {
        items: paginatedBooks,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Books by status retrieved', 'BookService', {
        status,
        total,
        page,
        itemCount: paginatedBooks.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in BookService.getByStatus', 'BookService', { 
        error: (error as Error).message,
        status,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching books by status' 
      };
    }
  }

  /**
   * Get books by minimum rating with pagination
   */
  async getByRating(minRating: number, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Book>>> {
    try {
      this.logger.debug('BookService.getByRating called', 'BookService', { minRating, params });

      if (typeof minRating !== 'number' || minRating < 1 || minRating > 5) {
        return { 
          success: false, 
          error: 'Invalid rating. Must be a number between 1 and 5' 
        };
      }

      const apiBooks = await publicAPI.getPublishedBooks();
      const filteredBooks = apiBooks
        .filter(book => book.overall_rating !== null && book.overall_rating >= minRating)
        .map(book => this.mapApiBook(book));

      // Sort by rating (highest first)
      const sortedBooks = filteredBooks.sort((a, b) => {
        const ratingA = a.overall_rating || 0;
        const ratingB = b.overall_rating || 0;
        return ratingB - ratingA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedBooks.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

      const result: PaginatedResult<Book> = {
        items: paginatedBooks,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Books by rating retrieved', 'BookService', {
        minRating,
        total,
        page,
        itemCount: paginatedBooks.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in BookService.getByRating', 'BookService', { 
        error: (error as Error).message,
        minRating,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching books by rating' 
      };
    }
  }

  /**
   * Get books by genre with pagination
   */
  async getByGenre(genre: string, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Book>>> {
    try {
      this.logger.debug('BookService.getByGenre called', 'BookService', { genre, params });

      if (!genre || typeof genre !== 'string') {
        return { success: false, error: 'Genre parameter is required' };
      }

      const apiBooks = await publicAPI.getPublishedBooks();
      const filteredBooks = apiBooks
        .filter(book => book.genre && book.genre.toLowerCase().includes(genre.toLowerCase()))
        .map(book => this.mapApiBook(book));

      // Sort by title
      const sortedBooks = filteredBooks.sort((a, b) =>
        a.title.localeCompare(b.title)
      );

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedBooks.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

      const result: PaginatedResult<Book> = {
        items: paginatedBooks,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Books by genre retrieved', 'BookService', {
        genre,
        total,
        page,
        itemCount: paginatedBooks.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in BookService.getByGenre', 'BookService', { 
        error: (error as Error).message,
        genre,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching books by genre' 
      };
    }
  }

  /**
   * Check if a book exists
   */
  async exists(id: string): Promise<ServiceResult<boolean>> {
    try {
      this.logger.debug('BookService.exists called', 'BookService', { id });

      if (!id || typeof id !== 'string') {
        return { success: false, error: 'Invalid book ID provided' };
      }

      const apiBooks = await publicAPI.getPublishedBooks();
      const exists = apiBooks.some(book => book.id === id);

      this.logger.debug('Book existence checked', 'BookService', { id, exists });

      return { success: true, data: exists };

    } catch (error) {
      this.logger.error('Error in BookService.exists', 'BookService', { 
        error: (error as Error).message,
        id 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while checking book existence' 
      };
    }
  }

  /**
   * Create a new book (placeholder - requires admin API)
   */
  async create(_book: Partial<Book>): Promise<ServiceResult<Book>> {
    this.logger.warn('BookService.create called but not implemented', 'BookService');
    return { 
      success: false, 
      error: 'Book creation is not available in the current implementation' 
    };
  }

  /**
   * Update a book (placeholder - requires admin API)
   */
  async update(_id: string, _updates: Partial<Book>): Promise<ServiceResult<Book>> {
    this.logger.warn('BookService.update called but not implemented', 'BookService');
    return { 
      success: false, 
      error: 'Book updates are not available in the current implementation' 
    };
  }

  /**
   * Delete a book (placeholder - requires admin API)
   */
  async delete(_id: string): Promise<ServiceResult<boolean>> {
    this.logger.warn('BookService.delete called but not implemented', 'BookService');
    return { 
      success: false, 
      error: 'Book deletion is not available in the current implementation' 
    };
  }

  /**
   * Apply sorting to books array
   */
  private applySorting(books: Book[], sortBy: string, sortOrder: string): Book[] {
    return books.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'author':
          comparison = a.author.localeCompare(b.author);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'overall_rating':
          comparison = (a.overall_rating || 0) - (b.overall_rating || 0);
          break;
        case 'end_date': {
          const dateA = a.end_date ? new Date(a.end_date).getTime() : 0;
          const dateB = b.end_date ? new Date(b.end_date).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
        default:
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }
} 